import React, { useEffect, useRef } from "react"
import _ from "lodash"
import { useDispatch, useSelector } from "react-redux"

import { useApi } from "@api"
import { IAppState, IPacksInfo } from "@store/appState"

import {
  receivedDailyPackStatusAction,
  receivedPacksAction,
  receivedStickersAction,
  receivedOpenPackStickersAction,
  receivedUnlockErrorAction,
  receivedOpenPackWaitAction,
  requestDailyPackStatusAction
} from "./actions"
import secondsLeft from "@helper/secondsLeft"
import { useTracking } from "@api/tracking"

const isError = (msg: Packs.Api.UnlockPackResponse): msg is Packs.Api.UnlockPackErrorResponse => {
  return !!(msg as Packs.Api.UnlockPackErrorResponse).error
}

// Keep this in sync with the Unity implementation
// See "Assets/Features/Tracking/Scripts/Helpers/CodeHelper.cs"
function determineRedeemCode(msg: Packs.Api.UnlockPackSuccessResponse) {
  if (msg.is_multi_code) {
    return msg.owner ? `promo_code_${msg.owner}` : `promo_code_unknown`
  }
  const market = msg.market
  if (!market || market === "") return "single_code_panini" // fallback
  if (market.length > 2) return `single_code_${market}`
  const firstLetter = market[0]
  switch(firstLetter) {
    case 'm': return "single_code_mcd";
    case 'p': return "single_code_panini";
    case 'c': return "single_code_coke";
    case 'f': return "single_code_fifa";
    default: return "single_code_unknown";
  }
}

export const usePacks = () => {
  const api = useApi<Packs.Api.Request, Packs.Api.Response>()
  const dispatch = useDispatch()
  const { track } = useTracking()

  const selected = useSelector<IAppState, {packs: IPacksInfo, initDone: boolean}>((state) => {
    return {
      packs: state.packs,
      initDone: state.initDone
    }
  })

  useEffect(() => { // repoll daily-packs-status when countdown over
    let interval: number

    if (!!selected.packs.receivedNextFreePackInfoAt && !_.isUndefined(selected.packs.nextFreePackInSeconds)) {
      interval = window.setInterval(() => {
        if (!selected.packs.hasNewPacksWaiting && secondsLeft(selected.packs.nextFreePackInSeconds as number, selected.packs.receivedNextFreePackInfoAt as Date) <= 0) {
          dispatch(requestDailyPackStatusAction())
        }
      }, 1000)
    }
    return () => { // cleanup
      window.clearInterval(interval)
    }
  }, [
    selected.packs.receivedNextFreePackInfoAt,
    selected.packs.nextFreePackInSeconds,
    selected.packs.hasNewPacksWaiting
  ])

  useEffect(() => {
    const listener = api.on((msg) => {
      if (msg.action === "daily_packs_status") {
        dispatch(receivedDailyPackStatusAction(msg))
      } else if (msg.action === "received_packs") {
        dispatch(receivedPacksAction(!selected.initDone, msg))
      } else if (msg.action === "received_stickers") {
        dispatch(receivedStickersAction(msg))
      } else if (msg.action === "unlock_pack") {
        if (isError(msg)) {
          dispatch(receivedUnlockErrorAction(msg))
          track({event: "redeem_code_ko"})
        } else {
          track({
            event: "activate_code",
            code_type: msg.is_multi_code ? "promo_code" : "single_code"
          })
          track({
            event: "redeem_code_ok",
            redeem_code: determineRedeemCode(msg)
          })
        }
      } else if (msg.action === "open_pack") {
        if (msg.stickers) {
          track({event: "open_standard_pack"})
          dispatch(receivedOpenPackStickersAction(msg.stickers))
        } else if (msg.wait) {
          dispatch(receivedOpenPackWaitAction(msg.wait))
        }
      } else if (msg.action === "received_achievements") {
        if (msg.achievements) {
          msg.achievements.forEach((achievement) => {
            if (achievement.type === "instant_pack" && achievement.args?.instant_pack === "premium") {
              track({event: "open_premium_pack"})
            }
          })
        }
      }
    })

    return () => { // cleanup
      api.off(listener)
    }
  }, [selected.initDone])
}
